import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Redirect } from 'react-router-dom';
import { gql } from '@apollo/client';
import { flowRight as compose } from 'lodash';

import { getUserAccessTokenCookie } from 'commons/localStorage';
import AdminPlaceholder from 'components/consumer/AdminPlaceholder';
import withApolloClient from 'components/hoc/withApolloClient';

import TrackOrderStatusForm from './form';
import TrackOrderStatusView from './view';

const GET_ORDER_TRACKING_WITH_ZIP = gql`
  query getOrderTrackingWithZip(
    $order_id: Int!
    $zip: SanitizedZipCodeInputString!
  ) {
    getOrderTrackingWithZip(order_id: $order_id, zip: $zip) {
      errors {
        code
        message
        field
      }
      order_id
      order_date
      rdc_city
      rdc_state
      display_ship_date
      display_arriving_date
      display_delivery_date
      carrier
      progress
      progress
      mainTitle
      displayDeliveryRange
      payment_type
      order {
        order_id
        order_type
        order_note
        order_status
        order_paynow
        payment_status
        delivery_date
        total_order_amount
        shipment_status
        shipping {
          address_name
          address_first_name
          address_last_name
          address_street1
          address_street2
          address_city
          address_country
          address_state
          address_zip_code
          address_phone
          address_fax
        }
        order_items {
          product_id
          pickLater
          product_type
          quantity
          quantity
          name
          total_cost
          variants_sku
          variants_text
          upholstery
          wood_stain
          option_value_ids
          goal_date
          eta_delivery_start
          eta_delivery_end
          eta_delivery_all
          shipping_status
          tracking_url
          tracking_note
          tracking_number
          carrier
          shipped_date_f
          small_image
          url_key
          sizes_text
          shipping_group
          delivery_method
          estimated_shipping
          shipped_date
          stock_discount
          estimated_delivery
          estimated_delivery_title
          options {
            id
            image
            name
            value
            optionTypeId
            optionTypeName
            default_value
            additional_cost
          }
          applied_protection_plan {
            plan_id
            plan_name
            plan_price
            plan_percent
            plan_description
          }
          available_protection_plans {
            plan_id
            plan_name
            plan_price
            plan_percent
            plan_description
          }
        }
      }
    }
  }
`;

const TrackOrderStatus = props => {
  const [setMode] = useState('form');
  const [isLoading, setIsLoading] = useState(false);
  const [setErrMsg] = useState('');

  const state = useCallback(() => {
    let tracking_info = null;
    if (props.location.state && props.location.state.tracking_info) {
      tracking_info = props.location.state.tracking_info;
    } else {
      tracking_info = null;
    }
    const theObj = {
      isLoading,
      tracking_info,
    };
    return theObj;
  }, [isLoading, props.location.state]);

  useEffect(() => {
    if (props.location.pathname === '/order-tracking/view/') setMode('view');
    else {
      setMode('form');
      const stateCopy = { ...state };
      delete stateCopy.tracking_info;
      props.history.replace({ ...props.history.location, stateCopy });
    }
  }, [
    props.history,
    props.location.pathname,
    props.location.state,
    setMode,
    state,
  ]);
  if (props.isAdmin) {
    return (
      <AdminPlaceholder>
        Placeholder for `Track Order Status` component. <br />
        The component will display correctly in Consumer
      </AdminPlaceholder>
    );
  }
  if (getUserAccessTokenCookie()) return <Redirect to="/customer/orders/" />;
  // if ('view' === mode && !props.location.state.tracking_info) return <Redirect to="/order-tracking/" />;

  const getOrderTrackingWithZipQuery = async (order_id, zip) => {
    try {
      let theOrder_id = order_id;
      theOrder_id = parseInt(theOrder_id, 10);
      return await props.client.query({
        query: GET_ORDER_TRACKING_WITH_ZIP,
        variables: {
          theOrder_id,
          zip,
        },
      });
    } catch (e) {
      return null;
    }
  };

  const actions = {
    resetError: () => {
      setErrMsg('');
      setIsLoading(false);
    },
    formSubmitHandle: async ({ order_id, zip }) => {
      setMode('form');
      setErrMsg('');
      setIsLoading(true);
      const queryLookup = await getOrderTrackingWithZipQuery(order_id, zip);
      if (queryLookup?.loading === false) {
        setIsLoading(false);
        if (queryLookup.data.getOrderTrackingWithZip.errors) {
          setErrMsg(
            'The order number does not match the billing zip code provided. Please double check and try again'
          );
        }
        if (queryLookup.data.getOrderTrackingWithZip.order_id) {
          setErrMsg('');
          props.history.push('/order-tracking/view/', {
            tracking_info: queryLookup.data.getOrderTrackingWithZip,
          });
        }
      }
    },
  };

  return (
    <div className="bg-gray-light9 md:px-0 md:py-12">
      {props.location.pathname === '/order-tracking/view/' &&
      state.tracking_info ? (
        <TrackOrderStatusView state={state} actions={actions} />
      ) : (
        <TrackOrderStatusForm state={state} actions={actions} />
      )}
    </div>
  );
};

TrackOrderStatus.propTypes = {
  isAdmin: PropTypes.bool,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    state: PropTypes.object,
  }),
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
    location: PropTypes.node,
  }),
  client: PropTypes.shape({
    query: PropTypes.func,
  }),
};

export default compose(withRouter, withApolloClient)(TrackOrderStatus);
